import { useEffect, useState } from 'react';

const CURRENT_KEY = 'wms.currentWarehouseId';
const DEFAULT_KEY = 'wms.defaultWarehouseId';

/**
 * 当前仓库上下文（Spec §5："选中的仓库上下文必须显而易见"）。
 *
 * `apiFetch`（lib/api.ts）是一个普通异步函数，不是 React 组件，无法用
 * `useContext` 读取——所以真相来源是一个模块级变量而不是 React context，
 * `useCurrentWarehouseId()` 只是订阅这个变量变化、驱动组件重渲染的薄封装
 * （与 TanStack Query 的 cache 是"服务端状态真相源、组件只是订阅者"同一思路）。
 *
 * 两层持久化：
 *   - sessionStorage：本标签页内"当前选中的仓库"，刷新页面不丢失，关闭标签页
 *     后清空——不需要用户每次刷新都重新选。
 *   - localStorage：仅当用户勾选"设为默认仓库"时才写入，下次登录自动跳过
 *     选择页直达仪表盘（F1 流程）。
 */
let currentWarehouseId: string | undefined =
  typeof window !== 'undefined' ? (sessionStorage.getItem(CURRENT_KEY) ?? undefined) : undefined;

const listeners = new Set<() => void>();

function setCurrent(id: string | undefined): void {
  currentWarehouseId = id;
  if (typeof window !== 'undefined') {
    if (id) sessionStorage.setItem(CURRENT_KEY, id);
    else sessionStorage.removeItem(CURRENT_KEY);
  }
  for (const listener of listeners) listener();
}

/** `apiFetch` 用这个同步读取当前仓库 id 来填充 X-Warehouse-Id 请求头 */
export function getCurrentWarehouseId(): string | undefined {
  return currentWarehouseId;
}

export function getDefaultWarehouseId(): string | undefined {
  return typeof window !== 'undefined'
    ? (localStorage.getItem(DEFAULT_KEY) ?? undefined)
    : undefined;
}

/** S2 仓库选择页 / 自动跳过逻辑共用的入口 */
export function selectWarehouse(id: string, remember: boolean): void {
  setCurrent(id);
  if (typeof window !== 'undefined') {
    if (remember) localStorage.setItem(DEFAULT_KEY, id);
    else localStorage.removeItem(DEFAULT_KEY);
  }
}

/** 登出时清空——下一个登录的用户不应该继承上一个人选的仓库 */
export function clearWarehouseSelection(): void {
  setCurrent(undefined);
  if (typeof window !== 'undefined') localStorage.removeItem(DEFAULT_KEY);
}

export function useCurrentWarehouseId(): string | undefined {
  const [id, setId] = useState(currentWarehouseId);
  useEffect(() => {
    const listener = (): void => setId(currentWarehouseId);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  return id;
}
